import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Settings as SettingsIcon, LayoutDashboard, FileText, Users, Settings, Menu, X, Sparkles, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";

const IssuerLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isAdmin = () => {
    const issuerData = localStorage.getItem('issuer_data');
    if (issuerData) {
      const data = JSON.parse(issuerData);
      return data.email === 'jdp@instructing.com';
    }
    return false;
  };

  const menuItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/issuer/dashboard" },
    ...(isAdmin() ? [{ icon: Settings, label: "🔧 Admin Templates", path: "/issuer/admin/templates" }] : []),
    { icon: FileText, label: "Create Certificate", path: "/issuer/create" },
    { icon: FileText, label: "Template Editor", path: "/issuer/template-editor" },
    { icon: Users, label: "View Certificates", path: "/issuer/certificates" },
    { icon: Settings, label: "Settings", path: "/issuer/settings" },
  ];

  const isActive = (path) => location.pathname === path;

  const handleStartWizard = () => {
    localStorage.removeItem('wizard_completed');
    localStorage.removeItem('wizard_seen');
    // Navigate to dashboard with wizard parameter
    navigate('/issuer/dashboard?wizard=true');
  };

  const handleLogout = () => {
    localStorage.removeItem('issuer_token');
    localStorage.removeItem('issuer_data');
    localStorage.removeItem('wizard_completed');
    localStorage.removeItem('wizard_seen');
    navigate('/issuer/login');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile Menu Button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <Button
          variant="outline"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="bg-white shadow-md"
          data-testid="mobile-menu-btn"
        >
          {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 h-screen w-64 bg-slate-900 text-slate-300 border-r border-slate-800 z-40 transform transition-transform duration-200 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
        data-testid="issuer-sidebar"
      >
        <div className="p-6">
          <div className="flex items-center gap-2 mb-8" onClick={() => navigate("/issuer/dashboard")} style={{cursor: 'pointer'}}>
            <SettingsIcon className="h-8 w-8 text-white" />
            <span className="text-xl font-semibold text-white" style={{fontFamily: 'Manrope'}}>Cert Machine</span>
          </div>

          <nav className="space-y-2">
            {menuItems.map((item, idx) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <button
                  key={idx}
                  onClick={() => {
                    navigate(item.path);
                    setSidebarOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg smooth-transition ${
                    active
                      ? 'bg-slate-800 text-white'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="text-sm font-medium">{item.label}</span>
                </button>
              );
            })}
          </nav>

          {/* Bottom Actions */}
          <div className="absolute bottom-6 left-6 right-6 space-y-2 border-t border-slate-800 pt-4">
            <button
              onClick={handleStartWizard}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-slate-800 hover:text-white smooth-transition"
              data-testid="nav-start-wizard"
            >
              <Sparkles className="h-5 w-5" />
              <span className="text-sm font-medium">Start Wizard</span>
            </button>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-400 hover:bg-red-900/20 hover:text-red-300 smooth-transition"
              data-testid="nav-logout"
            >
              <LogOut className="h-5 w-5" />
              <span className="text-sm font-medium">Log Out</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-overlay"
        ></div>
      )}

      {/* Main Content */}
      <main className="lg:ml-64 p-6 md:p-12">
        {children}
      </main>
    </div>
  );
};

export default IssuerLayout;
